"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import { getRaidEggImageSrc } from "~/lib/pokemon";

import { Card, CardHeader, CardTitle, CardContent } from "~/components/ui/card";

export interface RaidObj {
  uuid: string;
  pokemon_level: number | null;
  cp: number | null;
  move_1: number | null;
  move_2: number | null;
  last_seen: number | null;
}

interface RaidsCardProps {
  raidsData: Array<RaidObj> | undefined;
  title: string;
  i18n: {
    noResults: string;
    lastSeen: string;
    getTranslatedTier: (level: number) => string;
  };
}

const RaidsCard = ({ title, raidsData, i18n }: RaidsCardProps) => {
  const t = useTranslations("Pokemon");
  const data = raidsData ?? [];

  return (
    <Card className="col-span-12 lg:col-span-7" id={title}>
      <CardHeader className="pb-4">
        <CardTitle className="text-pory-blue-900 text-xl font-bold">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <div className="flex h-24 items-center justify-center rounded-lg border border-neutral-200 bg-neutral-50 shadow-sm">
            <p className="text-neutral-500">{i18n.noResults}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {data.map((raid) => {
              const tierName = raid.pokemon_level
                ? i18n.getTranslatedTier(raid.pokemon_level)
                : t("unknownTier");
              return (
                <div
                  key={raid.uuid}
                  className="flex items-center gap-4 rounded-xl border border-neutral-200 bg-white p-4 shadow-sm"
                >
                  <div className="relative flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-neutral-100 p-2">
                    <Image
                      src={getRaidEggImageSrc(raid.pokemon_level)}
                      alt={t("eggTier", { tier: tierName })}
                      width={48}
                      height={48}
                      className="object-contain drop-shadow-sm"
                    />
                  </div>
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <span className="text-pory-blue-900 font-black tracking-tight">
                        {tierName}
                      </span>
                      {raid.cp && (
                        <span className="bg-pory-pink-100 text-pory-pink-900 rounded-full px-2 py-0.5 text-xs font-bold">
                          {t("cpValue", { cp: raid.cp.toLocaleString() })}
                        </span>
                      )}
                    </div>
                    <span className="mt-1 text-xs font-medium text-neutral-500">
                      {i18n.lastSeen}:{" "}
                      <span suppressHydrationWarning>
                      {raid.last_seen
                        ? new Date(raid.last_seen).toLocaleDateString()
                        : "-"}
                      </span>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RaidsCard;
